import { useRouter } from 'next/router'
import { useState } from 'react'
import styles from './RegistrationSideCard.module.scss'
import { RegistrationSideCardProps } from './types'
import PriceDrawer from 'components/price-drawer/PriceDrawer'
import useWindowSize from 'hooks/useWindowSize'
import ProducerDrawer from 'components/producer-drawer/ProducerDrawer'
import { routes } from 'utils/routes'
import ProducerSliderLayout from 'components/producer-slider-layout/ProducerSliderLayout'
import SimulationCardContent from 'features/registration/components/registration-side-card/simulation-card-content/SimulationCardContent'
import FixedPriceDrawer from 'features/registration/components/registration-side-card/sales-prices-section/FixedPriceDrawer'
import EditInstalmentDrawer from 'features/registration/components/registration-side-card/price-simulation-section/edit-instalment-drawer/EditInstalmentDrawer'
import EditUsageDrawer from 'features/registration/components/registration-side-card/usage-section/edit-usage-drawer/EditUsageDrawer'
import { useStoreSelector } from 'hooks/store'
import ChangeProductDrawer from 'features/registration/components/change-product-drawer/ChangeProductDrawer'
import { checkHasPriceSimulationSection, checkHasEditUsageDrawer } from 'features/registration/utils'
import { SIMULATION_TYPES } from '@boltenergy-be/shared'
import { INTERNAL_REGISTRATION_URL_PARAMS } from 'constants/customer-flow'

export const RegistrationSideCard = ({ producer }: RegistrationSideCardProps) => {
  // Redux store
  const { confirmationFromPortalRegistration } = useStoreSelector((store) => store.marketing)
  const { chosenSimulationType } = useStoreSelector((store) => store.customer.inputs.simulation)

  // Next router
  const router = useRouter()
  const { locale, query } = router

  // Local state
  const [showPriceDrawer, setShowPriceDrawer] = useState<boolean>(false)
  const [showProducerDrawer, setShowProducerDrawer] = useState<boolean>(false)
  const [showChangeProductDrawer, setShowChangeProductDrawer] = useState<boolean>(false)
  const [showEditUsageDrawer, setShowEditUsageDrawer] = useState<boolean>(false)
  const [showEditInstalmentDrawer, setShowEditInstalmentDrawer] = useState<boolean>(false)
  const [showFixedPricesDrawer, setShowFixedPricesDrawer] = useState<boolean>(
    query[INTERNAL_REGISTRATION_URL_PARAMS.IS_CLOSED_WON] !== 'true' &&
      !confirmationFromPortalRegistration &&
      chosenSimulationType === SIMULATION_TYPES.fixedAmount
  )

  // Custom hooks
  const { isTablet } = useWindowSize()

  // Constants
  const shouldShowPriceSimulationSection = checkHasPriceSimulationSection(chosenSimulationType)
  const shouldShowEditUsageDrawer = checkHasEditUsageDrawer(chosenSimulationType)

  return (
    <>
      {!isTablet ? (
        <aside className={styles.container}>
          <SimulationCardContent
            {...{
              producer,
              setShowEditInstalmentDrawer,
              setShowEditUsageDrawer,
              setShowFixedPricesDrawer,
              setShowPriceDrawer,
              setShowProducerDrawer
            }}
          />
        </aside>
      ) : (
        <ProducerSliderLayout withPrice producer={producer}>
          <SimulationCardContent
            {...{
              producer,
              setShowEditInstalmentDrawer,
              setShowEditUsageDrawer,
              setShowFixedPricesDrawer,
              setShowPriceDrawer,
              setShowProducerDrawer
            }}
          />
        </ProducerSliderLayout>
      )}

      <PriceDrawer isOpen={showPriceDrawer} onRequestClose={() => setShowPriceDrawer(false)} producer={producer} />

      <ProducerDrawer
        isOpen={showProducerDrawer}
        onRequestClose={() => setShowProducerDrawer(false)}
        onSelectProducer={(p) => {
          setShowProducerDrawer(false)
          router.push(routes(locale, p.parentSlug).registration)
        }}
      />

      {shouldShowPriceSimulationSection && (
        <EditInstalmentDrawer isOpen={showEditInstalmentDrawer} onRequestClose={() => setShowEditInstalmentDrawer(false)} />
      )}

      {!shouldShowPriceSimulationSection && (
        <FixedPriceDrawer onRequesteClose={() => setShowFixedPricesDrawer(false)} isOpen={showFixedPricesDrawer} />
      )}

      {shouldShowEditUsageDrawer && <EditUsageDrawer isOpen={showEditUsageDrawer} onRequestClose={() => setShowEditUsageDrawer(false)} />}

      {shouldShowPriceSimulationSection && (
        <ChangeProductDrawer isOpen={showChangeProductDrawer} onRequestClose={() => setShowChangeProductDrawer(false)} />
      )}
    </>
  )
}

export default RegistrationSideCard
